import React, { useState, type FC } from 'react';
import { DefaultOptionType } from 'antd/es/select';
import { Modal, Select } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';
import { Form } from 'antd';
import { orderBy } from 'lodash';

import { DiscountType, type SystemUser, type JsonDataItem } from 'types';

export namespace AddSystemDiscountModal {
  const discountType = {
    'Dollar Discount': DiscountType.Dollar,
    'Percentage Discount': DiscountType.Percent,
    'Hours Discount': DiscountType.Time,
  };

  export type Props = {
    isOpen: boolean;
    onCancel: () => void;
    onOk: (discount: { type: number; approver: number }) => void;
    systemDiscounts: { discount_desc: string; customer_discount_id: number; discount_type: string }[];
    discountTypeTemplates: DefaultOptionType;
    discountApprovers: SystemUser[] | undefined;
    isLoading: boolean;
  };

  export const $: FC<Props> = observer((props) => {
    const { isOpen, onCancel, onOk, systemDiscounts, discountApprovers, discountTypeTemplates, isLoading } = props;
    const [selectedType, setSelectedType] = useState<DiscountType>();
    const [form] = Form.useForm();

    const handleValuesChange = (value: { type?: keyof typeof discountType }) => {
      if (value.type) {
        setSelectedType(discountType[value.type]);
        form.setFieldValue('reason', undefined);
      }
    };

    const handleFormSubmit = (values: JsonDataItem) => {
      onOk({
        type: values.reason,
        approver: values.approver,
      });
    };

    return (
      <Modal.$
        open={isOpen}
        title="Add System Discount"
        okText="Save"
        okButtonProps={{ disabled: isLoading }}
        onOk={form.submit}
        onCancel={onCancel}
        dataTestId="add-discount-modal"
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          onFinish={handleFormSubmit}
          onValuesChange={handleValuesChange}
        >
          <Select.$
            name="type"
            label="Discount Type"
            rules={[{ required: true, message: 'Please select a discount type!' }]}
            placeholder="Please select"
            dataTestId="discount-type"
            options={discountTypeTemplates?.map((option: { discount_desc: string }) => ({
              label: option.discount_desc,
              value: option.discount_desc,
            }))}
          />
          <Select.$
            name="reason"
            label="Reasons"
            rules={[{ required: true, message: 'Please select a reason!' }]}
            placeholder="Please select"
            dataTestId="select-reason"
            options={orderBy(
              systemDiscounts
                ?.filter((discount: DefaultOptionType) =>
                  selectedType ? discount.discount_type === selectedType : discount,
                )
                ?.map((option: { discount_desc: string; customer_discount_id: number }) => ({
                  label: option.discount_desc,
                  value: option.customer_discount_id,
                })),
              ['label'],
              ['asc'],
            )}
          />
          <Select.$
            name="approver"
            label="Approved By"
            rules={[{ required: true, message: 'Please select an approver!' }]}
            placeholder="Please select"
            dataTestId="discount-approver"
            options={discountApprovers?.map((option: { name: string; ID: number }) => ({
              label: option.name,
              value: option.ID,
            }))}
          />
        </Form>
      </Modal.$>
    );
  });
}
